import React, {useRef, useState} from 'react';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import {
  BalloonEditor as InlineEditor,
  Essentials,
  Heading,
  Table,
  TableCellProperties,
  Paragraph,
  Font,
  FontSize,
  Bold,
  Italic,
  Code,
  List,
  Indent,
  IndentBlock,
  CodeBlock,
  
  type EditorConfig
} from 'ckeditor5';
import { ChevronDown, ChevronUp } from 'lucide-react';

import 'ckeditor5/ckeditor5.css';

type EditorInstance = Awaited<ReturnType<typeof InlineEditor.create>>;

interface CKEditorReadonlyProps {
  content: string;
  className?: string;
  showToggleButton?: boolean;
  [key: string]: unknown;
}

const CKEditorReadonly: React.FC<CKEditorReadonlyProps> = ({
                                                             content,
                                                             className = '',
                                                             showToggleButton = true,
                                                             ...props
                                                           }) => {
  const editorRef = useRef<EditorInstance | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };
  
  const getViewerClass = () => {
    if (!showToggleButton) {
      return 'ck-viewer__full';
    }
    return isExpanded ? 'ck-viewer__expanded' : 'ck-viewer__collapse';
  };
  
  const editorConfiguration: EditorConfig = {
    licenseKey: 'GPL',
    plugins: [
      Essentials,
      Heading,
      Table,
      TableCellProperties,
      Paragraph,
      Font,
      FontSize,
      Bold,
      Italic,
      Code,
      List,
      Indent,
      IndentBlock,
      CodeBlock
    ],
    toolbar: [],
  };
  
  return (
    <div className={`${className}`}>
      <div
        className={`ck-viewer__main ${getViewerClass()} border border-gray-300 rounded w-full transition-all duration-500 ease-in-out relative overflow-hidden`}
      >
        <div className="px-0 py-0 mb-10">
          <CKEditor
            editor={InlineEditor}
            config={editorConfiguration}
            data={content || ""}
            onReady={editor => {
              editorRef.current = editor as EditorInstance;
              editor.enableReadOnlyMode("preview");
              
              // console.log('CKEditor5 Readonly Component is ready!', editor);
            }}
            onError={(error) => {
              console.error('CKEditor Readonly Error:', error);
            }}
            {...props}
          />
        </div>
        
        {showToggleButton && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-transparent">
            <div className="flex justify-center pb-2 pt-6">
              <button
                onClick={toggleExpand}
                className="group inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-white border border-gray-200/60 hover:border-gray-300/80 rounded-full shadow-sm hover:shadow-md transition-all duration-300 ease-out transform hover:scale-105"
              >
                <span className="transition-all duration-300 ease-out">
                  {isExpanded ? 'Show Less' : 'Show More'}
                </span>
                <div className="transition-all duration-300 ease-out group-hover:translate-y-0.5">
                  {isExpanded ? (
                    <ChevronUp className="w-3 h-3 transition-transform duration-300 ease-out group-hover:-translate-y-0.5" />
                  ) : (
                    <ChevronDown className="w-3 h-3 transition-transform duration-300 ease-out group-hover:translate-y-[-1.7px]" />
                  )}
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CKEditorReadonly;